import { MailOutlined, CheckCircleOutlined } from '@mui/icons-material';
import { Button, CardContainer } from 'finara-components';
import { useState } from 'react';
import toast from 'react-hot-toast';

import { useCreateInvitation } from '@/hooks/queries/useAdminOnboarding';

interface InviteAdminModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const InviteAdminModal: React.FC<InviteAdminModalProps> = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);

  const createInvitation = useCreateInvitation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    try {
      await createInvitation.mutateAsync({ email: email.trim() });
      setSent(true);
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const handleClose = () => {
    setEmail('');
    setSent(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black opacity-60 transition-opacity"
        onClick={handleClose}
        role="button"
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === 'Escape') handleClose();
        }}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <CardContainer
          className="bg-surface !opacity-100 relative max-w-md w-full"
          onClick={e => e.stopPropagation()}
        >
          <div className="p-6">
            {sent ? (
              /* Success state */
              <div className="text-center py-4">
                <CheckCircleOutlined className="text-green-500 mb-3" style={{ fontSize: 48 }} />
                <h2 className="text-lg font-bold text-brand-white mb-2">Invitación enviada</h2>
                <p className="text-sm text-muted mb-1">Se envió un enlace de registro a:</p>
                <p className="text-sm font-medium text-brand-white mb-6">{email}</p>
                <p className="text-xs text-muted mb-6">
                  El enlace expira en 7 días. El cliente deberá completar el proceso de onboarding
                  antes de poder acceder a la plataforma.
                </p>
                <Button variant="black" onClick={handleClose} className="w-full">
                  Cerrar
                </Button>
              </div>
            ) : (
              /* Form state */
              <>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-blue-500/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <MailOutlined className="text-brand-blue" fontSize="small" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-brand-white">Invitar Cliente</h2>
                    <p className="text-xs text-muted">
                      El cliente completará su registro y KYC antes de acceder
                    </p>
                  </div>
                </div>

                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-md p-3 mb-5">
                  <p className="text-xs text-yellow-400">
                    <strong>Flujo de onboarding:</strong> El cliente recibirá un enlace para crear
                    su cuenta, completar 6 pasos de información y documentos, y esperar la
                    aprobación del equipo Finara.
                  </p>
                </div>

                <form onSubmit={e => void handleSubmit(e)}>
                  <div className="mb-5">
                    <label
                      htmlFor="invite-email"
                      className="block text-sm font-medium text-brand-white mb-1"
                    >
                      Email del cliente *
                    </label>
                    <input
                      id="invite-email"
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      className="w-full px-3 py-2 border border-border bg-surface text-brand-white placeholder:text-muted rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-brand-blue focus:border-brand-blue"
                      placeholder="cliente@empresa.com"
                      required
                    />
                  </div>

                  <div className="flex gap-3">
                    <Button
                      type="submit"
                      variant="black"
                      disabled={createInvitation.isPending}
                      className="flex-1"
                    >
                      {createInvitation.isPending ? 'Enviando...' : 'Enviar invitación'}
                    </Button>
                    <Button
                      type="button"
                      variant="white"
                      onClick={handleClose}
                      disabled={createInvitation.isPending}
                      className="flex-1"
                    >
                      Cancelar
                    </Button>
                  </div>
                </form>
              </>
            )}
          </div>
        </CardContainer>
      </div>
    </div>
  );
};
